import smoothscroll from "smoothscroll-polyfill";
import { useEffect, useRef, useState } from "react";
import {
  NO_VARIANT_CODE,
  SIZE_SELECTOR_ERROR_NO_SIZE_SELECTED,
  WARNING_OR_ERROR_EVENT,
  WISHLIST_NO_VARIANT_CODE,
  ADD_TO_WISHLIST_ESI_WARNING_OR_ERROR,
} from "../../../../constants/constants";
import { scrollIntoViewAndWait } from "@/utils/scroll/scroll";
import { Alert } from "@JohnLewisPartnership/jl-design-system/dist/components/alert/Alert";

import styles from "./SizeError.module.scss";

type SizeErrorProps = {
  sizes: {
    name: string | null | undefined;
  }[];
};

export const SizeError = ({ sizes }: SizeErrorProps) => {
  const [hasSizeError, setHasSizeError] = useState(false);
  const [errorMessageText, setErrorMessageText] = useState("");
  const errorMessageRef = useRef<HTMLDivElement | null>(null);

  const scrollToError = () => {
    setTimeout(() => {
      smoothscroll.polyfill();
      const sizeErrorElement = errorMessageRef.current;

      if (sizeErrorElement) {
        scrollIntoViewAndWait(sizeErrorElement, {
          behavior: "smooth",
        }).then(() => {
          document.getElementsByName("size:error:message")[0]?.focus();
        });
      }
    }, 0);
  };

  useEffect(() => {
    setHasSizeError(false);
    setErrorMessageText("");
  }, [sizes]);

  useEffect(() => {
    const handleNoSizeSelectedError = () => {
      setHasSizeError(true);
      setErrorMessageText("Select a size");
      scrollToError();
    };

    const handleWishlistNoSizeSelectedError = () => {
      setHasSizeError(true);
      setErrorMessageText("Please choose a size first");
      scrollToError();
    };

    const errorOrWarningHandler = (
      event: CustomEvent<{ errors: { code: string }[] }>,
    ) => {
      const data = event.detail;
      const { errors = [] } = data;

      const hasBasketVariantError = errors.some(
        ({ code }) => code === NO_VARIANT_CODE,
      );

      const hasWishlistVariantError = errors.some(
        ({ code }) => code === WISHLIST_NO_VARIANT_CODE,
      );

      if (hasBasketVariantError) {
        setHasSizeError(true);
        setErrorMessageText("Select a size");
      }

      if (hasWishlistVariantError) {
        setHasSizeError(true);
        setErrorMessageText("Please choose a size first");
      }
    };

    window.addEventListener(
      SIZE_SELECTOR_ERROR_NO_SIZE_SELECTED,
      handleNoSizeSelectedError,
    );

    window.addEventListener(
      ADD_TO_WISHLIST_ESI_WARNING_OR_ERROR,
      handleWishlistNoSizeSelectedError,
    );

    window.addEventListener(WARNING_OR_ERROR_EVENT, errorOrWarningHandler);

    return () => {
      window.removeEventListener(
        SIZE_SELECTOR_ERROR_NO_SIZE_SELECTED,
        handleNoSizeSelectedError,
      );

      window.removeEventListener(
        ADD_TO_WISHLIST_ESI_WARNING_OR_ERROR,
        handleWishlistNoSizeSelectedError,
      );

      window.removeEventListener(WARNING_OR_ERROR_EVENT, errorOrWarningHandler);
    };
  }, []);

  if (sizes.length == 0) {
    return null;
  }

  return (
    <>
      {hasSizeError && (
        <div id="size:error" data-testid="size:error" ref={errorMessageRef}>
          <Alert
            className={styles.message}
            body={errorMessageText}
            type="error"
          />
        </div>
      )}
    </>
  );
};
